import cs from 'classnames';
import React, { useMemo } from 'react';
import { injectStoresV2 } from '../../stores/injectStoresV2';
import { ExperimentsStore } from '../../../stores/ExperimentsStore';
import { ConfigStore } from '../../stores/ConfigStore';
import { Search } from '../Search/Search';
import { HeaderStore } from '../../stores/HeaderStore';
import { RoutingStore } from '../../stores/RoutingStore';
import { WithTranslations, withTranslations } from '../../../utils/withTranslations';
import { headerTitleDataHooks } from './HeaderTitle.dataHooks';
import styles from './HeaderTitle.scss';

interface HeaderTitleProps extends WithTranslations {
  children?: React.ReactNode;
  dataHook?: string;
  focusOnSubTitle?: () => void;
  headerStore: HeaderStore;
  routingStore: RoutingStore;
  experimentsStore: ExperimentsStore;
  configStore: ConfigStore;
}

const HeaderTitleCmp: React.FC<HeaderTitleProps> = ({
  t,
  dataHook,
  headerStore,
  routingStore,
  configStore,
  experimentsStore,
}) => {
  const HeadingTagName = routingStore.matchedRoute.routeName === 'home' ? 'h1' : 'div';
  const { isLargeSearchInput, isSearchInputFocused } = headerStore;
  const isNewUi = configStore.config.newUI;
  const isNewUiWithAnimation =
    (experimentsStore.is('specs.funnel.templatePageUiV2WithAnimation', 'true') ||
      experimentsStore.is('specs.funnel.templatePageUiV2', 'true')) &&
    !experimentsStore.is('specs.funnel.templatePageUiV2Fixes', 'true');

  const uiFixes =
    experimentsStore.is('specs.funnel.templatePageUiV2Fixes', 'true') ||
    experimentsStore.is('specs.funnel.templatePageUiV2WithAnimation', 'true');

  const animatedTitle = useMemo(() => {
    if (isNewUiWithAnimation) {
      const words = t('header.title.v2').split(' ');

      return (
        <>
          <div className={styles.staggeredText}>
            {words.map((word, i) => (
              <span key={i} style={{ ['--i' as string]: i }}>
                {word}
              </span>
            ))}
          </div>
        </>
      );
    }
    return null;
  }, [isNewUiWithAnimation, t]);

  return (
    <div
      className={cs(styles.header, { [styles.column]: isLargeSearchInput || uiFixes, [styles.newHeader]: isNewUi })}
      data-hook={dataHook}
      style={{ ['--words-length' as string]: t('header.title.v2').split(' ').length }}
    >
      <div className={styles.titleContainer}>
        <HeadingTagName
          className={cs(styles.title, {
            [styles.smallText]: isSearchInputFocused,
            [styles.centerAligned]: isLargeSearchInput || uiFixes,
            [styles.newTitle]: isNewUi && !isNewUiWithAnimation,
          })}
          data-hook={headerTitleDataHooks.title()}
        >
          {isNewUiWithAnimation ? (
            <div className={styles.newTitleAnimation}>{t('header.title.v2')}</div>
          ) : (
            t('header.title.v2')
          )}
        </HeadingTagName>
        {animatedTitle}
      </div>
      {!headerStore.isSticky && <Search dataHook={headerTitleDataHooks.search()} />}
    </div>
  );
};

export const HeaderTitle = withTranslations(
  injectStoresV2('headerStore', 'routingStore', 'experimentsStore', 'configStore')(HeaderTitleCmp),
);
